import React from 'react';
import type { ColumnDef, Row, StockFeatures } from '@tanstack/react-table';
import { Banner, DataTable, Heading, Page, PageHeader, TagBadge, Text } from '@chatsdet/ui';
import { Robot } from '@phosphor-icons/react';
import type { AgentEvalCaseResult, AgentEvalRunResult } from '@chatsdet/types/agent-eval-result';
import { meanTaskTokens } from '@chatsdet/types/agent-eval-render';
import type { AgentEvalRunDetail } from '../../../features/agent-evals/useAgentEvalRun';
import {
  formatCostCents,
  formatPercent,
  formatTokens,
  runVerdictPresentation,
  shortSha,
} from '../../../features/agent-evals/agentEvalPresentation';
import { formatDurationMs } from '../../../lib/formatDuration';
import { AgentEvalAttemptPanel, Pre, Transcript } from './AgentEvalAttemptPanel';

const numeric = 'tabular-nums';

const COLUMNS: ColumnDef<StockFeatures, AgentEvalCaseResult>[] = [
  {
    id: 'case',
    header: 'Case',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="min-w-0">
        <Text size="sm" className="truncate font-mono">{row.original.key}</Text>
        <Text size="xs" className="truncate text-muted-foreground">{row.original.title}</Text>
      </div>
    ),
    meta: { flex: 3, minWidth: 220 },
  },
  {
    id: 'result',
    header: 'Result',
    enableSorting: false,
    cell: ({ row }) => (
      <TagBadge name={row.original.passed ? 'Pass' : 'Fail'} color={row.original.passed ? 'green' : 'red'} size="sm" />
    ),
    meta: { minWidth: 90 },
  },
  {
    id: 'attempts',
    header: 'Attempts passed',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className={numeric}>{`${row.original.passedAttempts}/${row.original.attempts.length}`}</Text>
    ),
    meta: { minWidth: 130 },
  },
  {
    id: 'score',
    header: 'Mean score',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className={numeric}>{Number(row.original.meanScore.toFixed(2))}</Text>,
    meta: { minWidth: 100 },
  },
  {
    id: 'cost',
    header: 'Cost/attempt',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className={numeric}>{formatCostCents(row.original.costCentsPerAttempt, row.original.attempts.some((a) => !a.costComplete))}</Text>,
    meta: { minWidth: 110 },
  },
  {
    id: 'time',
    header: 'Time/attempt',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className={numeric}>{formatDurationMs(row.original.durationMsPerAttempt)}</Text>,
    meta: { minWidth: 110 },
  },
  {
    id: 'tokens',
    header: 'Tokens in/out',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className={numeric}>{formatTokens(row.original.tokensPerAttempt)}</Text>,
    meta: { flex: 2, minWidth: 160 },
  },
];

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  const id = React.useId();
  return (
    <div className="space-y-0.5">
      <dt id={id} className="text-xs text-muted-foreground">{label}</dt>
      <dd aria-labelledby={id} className="text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function Totals({ result }: { result: AgentEvalRunResult }) {
  const summary = result.summary;
  const incomplete = summary.incompleteCalls > 0;
  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      <Stat label="Cost per task" value={formatCostCents(summary.costCentsPerTask, incomplete)} />
      <Stat label="Time per task" value={formatDurationMs(summary.durationMsPerTask)} />
      <Stat label="Tokens per task" value={formatTokens(meanTaskTokens(result))} />
      <Stat label="Total cost" value={formatCostCents(summary.costCents, incomplete)} />
      <Stat label="Judge cost" value={`${formatCostCents(summary.judge.costCents)} (${summary.judge.calls} calls, not included above)`} />
      {summary.userSim && (
        <Stat label="User simulator cost" value={`${formatCostCents(summary.userSim.costCents)} (${summary.userSim.calls} calls, not included above)`} />
      )}
    </dl>
  );
}

function Headline({ run, result }: { run: AgentEvalRunDetail; result: AgentEvalRunResult | null }) {
  const badge = runVerdictPresentation(run);
  const summary = result?.summary;
  return (
    <PageHeader
      icon={<Robot size={24} weight="fill" />}
      title={result?.eval ?? 'Agent eval run'}
      eyebrow={
        <span data-testid="agent-eval-verdict">
          <TagBadge name={badge.label} color={badge.color} size="md" />
        </span>
      }
      description={
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <span>{`${run.modelAlias} (${run.model})`}</span>
          <span>
            commit <span title={run.commitSha} className="font-mono text-foreground">{shortSha(run.commitSha)}</span>
          </span>
          {result && summary && (
            <>
              <span>{`${formatPercent(summary.passRate)} pass rate, bar ${formatPercent(result.passBar)}`}</span>
              <span>{`${summary.casesPassed}/${summary.cases} cases`}</span>
              <span>{`${result.repeats} repeats, pass at least ${result.passAtLeast}`}</span>
            </>
          )}
        </span>
      }
    />
  );
}

/** The read-only run page body: headline, totals, and the case table expanding to each attempt. */
export function AgentEvalRunView({ run }: { run: AgentEvalRunDetail }) {
  const result = run.result;
  // Every attempt row exists from the start of a run; only ones that recorded something are evidence.
  const recorded = run.attempts.filter((a) => a.answer || a.stdoutTail || a.transcript?.length);
  return (
    <Page>
      <Headline run={run} result={result} />
      {(run.error || run.status === 'failed') && <Banner variant="error">{run.error ?? 'The run failed.'}</Banner>}
      {!result && recorded.length > 0 && (
        <section className="space-y-3">
          <Heading level={3}>Recorded attempts</Heading>
          {recorded.map((a) => (
            <div key={`${a.caseKey}-${a.attemptIndex}`} className="space-y-2">
              <Text size="sm" className="font-mono">{`${a.caseKey} attempt ${a.attemptIndex}`}</Text>
              {a.answer && <Pre label="Answer">{a.answer}</Pre>}
              {a.stdoutTail && <Pre label="Stdout tail">{a.stdoutTail}</Pre>}
              {a.transcript && <Transcript entries={a.transcript} />}
            </div>
          ))}
        </section>
      )}
      {result && (
        <>
          <Totals result={result} />
          {result.warnings.length > 0 && (
            <Banner variant="warning">
              <ul>
                {result.warnings.map((warning, i) => (
                  <li key={`${i}-${warning}`}>{warning}</li>
                ))}
              </ul>
            </Banner>
          )}
          <section className="space-y-2">
            <Heading level={3}>Cases</Heading>
            <DataTable
              columns={COLUMNS}
              data={result.cases}
              aria-label="Cases"
              getRowId={(testCase) => testCase.key}
              pageSize={50}
              expandableRows={{
                render: (row: Row<StockFeatures, AgentEvalCaseResult>) => (
                  <div className="divide-y divide-border px-4 py-3">
                    {row.original.attempts.map((attempt) => (
                      <AgentEvalAttemptPanel
                        key={attempt.index}
                        attempt={attempt}
                        detail={run.attempts.find(
                          (d) => d.caseKey === row.original.key && d.attemptIndex === attempt.index
                        )}
                      />
                    ))}
                  </div>
                ),
              }}
            />
          </section>
        </>
      )}
    </Page>
  );
}
